// Centralized experience data — 2026 official spec.
// Pricing math (verified): Royal Essence 249.99 - 75.00 = 174.99
// Sovereign 149.99 - 50.00 = 99.99 · Majestic Vibes 149.99 - 50.00 = 99.99
//
// All three experiences are different CREATIVE PATHS to the same finished
// physical product: a 16" × 20" framed canvas.
//
// Imagery: REAL Crowned assets only. Royal Essence uses actual Crowned-designed
// artwork (Jackson Trio — presented as an artwork example, NOT a customer claim).
// Sovereign intentionally uses no photograph in its hero — the customer brings
// the photo — and features the real Crowned creation-process video lower on-page.

export const FINISHED_PIECE = '16" × 20" Framed Canvas';

const ROYAL_ESSENCE_IMG = "/royal-essence-artwork.webp";
const MAJESTIC_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4eaafebf-5e2b-455c-be92-0f4fe62ed043/images/f27f3896403e05c9e4ded49a844832aa892a4c1ba437c6f644cffd0cad1ce64f.png";

const SHARED_PROCESS_TAIL = [
  { step: "Artwork Creation", note: "Crowned builds your custom artwork around the story you shared." },
  { step: "First Proof", note: "You receive the first proof of your masterpiece for review." },
  { step: "Two Complimentary Revision Rounds", note: "Up to two revision rounds are included with every experience." },
  { step: "Final Approval", note: "Nothing goes to production until you approve the final artwork." },
  { step: "Remaining Payment", note: "Your remaining balance is invoiced after final approval, before production." },
  { step: "Production", note: "Approved artwork moves into museum-grade canvas production." },
  { step: "Shipping & Delivery", note: "Your finished Crowned piece is packaged with care and shipped to your door." },
];

export const PRODUCTS = {
  "royal-essence": {
    id: "royal-essence",
    slug: "royal-essence",
    badge: "The Complete Crowned Experience",
    fullName: "The Royal Essence Experience",
    shortName: "Royal Essence",
    headline: "Your moment. Your shoot. Your masterpiece.",
    ctaLabel: "Begin Your Royal Essence",
    checkoutPackageId: "royal-essence-deposit",
    image: ROYAL_ESSENCE_IMG,
    imageAlt:
      "Crowned-designed artwork example — a finished Crowned creation in the artistic direction of the Royal Essence experience",
    finishedPiece: FINISHED_PIECE,
    pricing: {
      total: "249.99",
      initial: "75.00",
      remaining: "174.99",
      due: "After final approval of your artwork, before production.",
    },
    positioning: [
      "Royal Essence is for the customer who wants Crowned involved from the beginning. Instead of searching through your phone for the perfect photograph, Royal Essence begins with photography intentionally created with your finished Crowned masterpiece in mind.",
      "Photography. Story. Creative direction. Custom artwork. One complete Crowned experience, built around you.",
    ],
    whoFor: [
      "You want the complete Crowned experience — photography included — rather than supplying your own photo.",
      "You're marking a milestone, a relationship, a business moment or a personal achievement that deserves intentional photography.",
      "You want creative direction from the first frame to the final canvas.",
      "You're in or able to coordinate with the Madison, Wisconsin area for the photography session.",
    ],
    differentTitle: "What makes Royal Essence different.",
    different: [
      "The photograph is created for the artwork — not the other way around. Every frame is shot with your finished masterpiece in mind.",
      "Crowned is involved from the very beginning: story, styling, creative direction and capture.",
      "Your experience includes the Crowned Creative Questionnaire, a first proof, and two complimentary revision rounds before production.",
    ],
    photographyNote:
      "Royal Essence photography is coordinated directly with you after your initial payment. Session location, scheduling and availability are confirmed together — photography is anchored to the Madison, Wisconsin area and surrounding region by arrangement.",
    includes: [
      "Photography session created with your finished artwork in mind",
      "Crowned Creative Questionnaire and personal creative direction",
      "Custom artwork built around your story",
      "First proof with two complimentary revision rounds",
      `Your finished Crowned piece — a ${FINISHED_PIECE}`,
      "Shipping and delivery to your door",
    ],
    process: [
      { step: "Initial Payment", note: "Your $75.00 initial payment officially begins your Royal Essence experience." },
      { step: "Creative Questionnaire", note: "Tell us your story, your vision and what this moment means." },
      { step: "Photography Experience", note: "We coordinate location and scheduling with you, then create photography intentionally shot for your finished masterpiece." },
      ...SHARED_PROCESS_TAIL,
    ],
    faqs: [
      {
        q: "Is $75.00 the full price?",
        a: "No. $75.00 is the initial payment that begins your experience. The total Royal Essence experience is $249.99, and the remaining $174.99 balance is invoiced after final approval of your artwork, before production.",
      },
      {
        q: "Where does the photography happen?",
        a: "Photography location, scheduling and availability are coordinated and confirmed with you directly after your initial payment. Royal Essence photography is anchored to the Madison, Wisconsin area and surrounding region by arrangement.",
      },
      {
        q: "What happens after I pay?",
        a: "You'll be directed to your Crowned Creative Questionnaire — the detailed brief that tells us your story. From there we coordinate your photography experience and begin creating.",
      },
      {
        q: "How many revisions are included?",
        a: "Every Crowned experience includes a first proof and up to two complimentary revision rounds. Nothing goes to production until you give final approval.",
      },
    ],
  },

  sovereign: {
    id: "sovereign",
    slug: "sovereign",
    badge: "Your Photograph, Crowned",
    fullName: "The Sovereign Canvas Experience",
    shortName: "Sovereign Canvas",
    headline: "You already have the photo. Let's turn it into something greater.",
    ctaLabel: "Create My Sovereign Canvas",
    checkoutPackageId: "sovereign-start",
    image: null, // Customer brings the photo — visual is a Crowned frame, not stock.
    imageAlt: "An empty Crowned frame awaiting your photograph",
    finishedPiece: FINISHED_PIECE,
    pricing: {
      total: "149.99",
      initial: "50.00",
      remaining: "99.99",
      due: "After final proof approval, before production.",
    },
    positioning: [
      "Some photographs already carry the perfect moment. Sovereign Canvas is for customers who already have a meaningful photograph they want transformed into personalized Crowned artwork.",
      "You bring the photograph. You tell us the story. We build the artwork around it.",
    ],
    whoFor: [
      "You already have the photograph — a person, a moment, a milestone — and you want it transformed into custom artwork.",
      "You're honoring a relationship, a graduation, a family, a comeback, or a loved one.",
      "You want the Crowned treatment without scheduling a photography session.",
      "You want to commission from anywhere — Sovereign Canvas is fully online.",
    ],
    differentTitle: "Why the photograph matters.",
    different: [
      "Your photograph is the foundation of your finished artwork. The stronger the photograph, the stronger the masterpiece.",
      "Crowned doesn't simply print your photo — we learn the story behind it and build the artwork around that meaning.",
      "Not sure your photo is ready? The free Crowned Perfect Photo Guide shows you exactly what we look for before you submit.",
    ],
    photoRequirements: [
      "The highest resolution version you have — original files beat screenshots.",
      "Clear focus on the person or moment that matters most.",
      "Good, natural lighting — avoid heavy filters and dark, grainy shots.",
      "A photograph that means something. The story matters as much as the pixels.",
    ],
    includes: [
      "Custom artwork built from your meaningful photograph",
      "Crowned Creative Questionnaire and creative direction",
      "First proof with two complimentary revision rounds",
      `Your finished Crowned piece — a ${FINISHED_PIECE}`,
      "Shipping and delivery to your door",
    ],
    process: [
      { step: "Initial Payment", note: "Your $50.00 initial payment officially begins your Sovereign Canvas experience." },
      { step: "Creative Questionnaire", note: "Tell us the story behind the photograph and the vision for the piece." },
      { step: "Photo Submission", note: "Send us your photograph through your submission links — our studio reviews it and confirms fit." },
      ...SHARED_PROCESS_TAIL,
    ],
    faqs: [
      {
        q: "Is $50.00 the full price?",
        a: "No. $50.00 is the initial payment that begins your experience. The total Sovereign Canvas experience is $149.99, and the remaining $99.99 balance is invoiced after final proof approval, before production.",
      },
      {
        q: "How do I know if my photo is good enough?",
        a: "Start with the free Crowned Perfect Photo Guide — it shows you exactly what makes a photograph stronger for custom artwork. If your photo needs attention, our studio will tell you honestly after submission.",
      },
      {
        q: "How do I send my photo?",
        a: "After your initial payment you'll complete the Creative Questionnaire and submit your photo through shareable links (Google Drive, Dropbox, iCloud or any public URL).",
      },
      {
        q: "How many revisions are included?",
        a: "A first proof plus up to two complimentary revision rounds. Production only begins after your final approval.",
      },
    ],
  },

  "majestic-vibes": {
    id: "majestic-vibes",
    slug: "majestic-vibes",
    badge: "Pure Creative Vision",
    fullName: "The Majestic Vibes Experience",
    shortName: "Majestic Vibes",
    headline: "Your vision doesn't have to start with a photograph.",
    ctaLabel: "Create My Majestic Vibe",
    checkoutPackageId: "majestic-vibes-start",
    image: MAJESTIC_IMG,
    imageAlt:
      "Crowned Canvas Creations Majestic Vibes artwork — flowing abstract gold linework on deep plum",
    finishedPiece: FINISHED_PIECE,
    pricing: {
      total: "149.99",
      initial: "50.00",
      remaining: "99.99",
      due: "After final proof approval, before production.",
    },
    positioning: [
      "Majestic Vibes begins with imagination. A feeling. A mood. A message. A room. An aesthetic. A cultural concept. A personal vision.",
      "You provide the inspiration. Crowned creates the vibe.",
    ],
    whoFor: [
      "You have an idea, an aesthetic or a message — not a photograph.",
      "You're styling a room, an office, a studio or a space that needs a statement piece.",
      "You want artwork built around a cultural concept, a personal mantra or a creative vision.",
      "You want something one-of-one, created for you and nobody else.",
    ],
    differentTitle: "What makes Majestic Vibes different.",
    different: [
      "No photograph required — the experience begins with pure creative direction.",
      "You set the mood, palette and message. Crowned translates it into custom artwork.",
      "Every Majestic Vibes piece is built from your Creative Questionnaire, proofed with you, and revised until it feels right.",
    ],
    inspirationNote:
      "Bring references, colors, words, a room photo, a mood board, a lyric, a memory — anything that carries the feeling. Your Creative Questionnaire turns that inspiration into direction our studio can build from.",
    includes: [
      "Original custom artwork created from your vision",
      "Crowned Creative Questionnaire and creative direction",
      "First proof with two complimentary revision rounds",
      `Your finished Crowned piece — a ${FINISHED_PIECE}`,
      "Shipping and delivery to your door",
    ],
    process: [
      { step: "Initial Payment", note: "Your $50.00 initial payment officially begins your Majestic Vibes experience." },
      { step: "Creative Questionnaire", note: "Share the vision — mood, palette, message, references and the space it will live in." },
      { step: "Creative Direction", note: "We align on direction before artwork development begins." },
      ...SHARED_PROCESS_TAIL,
    ],
    faqs: [
      {
        q: "Is $50.00 the full price?",
        a: "No. $50.00 is the initial payment that begins your experience. The total Majestic Vibes experience is $149.99, and the remaining $99.99 balance is invoiced after final proof approval, before production.",
      },
      {
        q: "What if I only have a rough idea?",
        a: "That's exactly what Majestic Vibes is for. The Creative Questionnaire is built to draw the vision out — references, colors, words and feelings are enough to begin.",
      },
      {
        q: "Can I request specific colors or a theme?",
        a: "Yes. Palette, mood, message and cultural references all belong in your Creative Questionnaire — that's the raw material for your piece.",
      },
      {
        q: "How many revisions are included?",
        a: "A first proof plus up to two complimentary revision rounds. Production only begins after your final approval.",
      },
    ],
  },
};

export const PRODUCT_LIST = [
  PRODUCTS["royal-essence"],
  PRODUCTS["sovereign"],
  PRODUCTS["majestic-vibes"],
];
